import React, { useEffect, useRef, useState } from "react";

/**
 * Lean rich-text editor for value-based offer sections.
 *
 * - contentEditable div, preserves whitespace via CSS (white-space: pre-wrap)
 * - No visible toolbar — formatting works via the browser's native shortcuts:
 *     ⌘B / Ctrl+B → bold
 *     ⌘I / Ctrl+I → italic
 *     ⌘U / Ctrl+U → underline
 *   contentEditable hands these to the browser automatically.
 * - Stores sanitized inline HTML (<p>, <br>, <strong>, <em>, <u>, <ul>, <ol>, <li>)
 * - Emits onChange(html) on input/blur. Paste is forced to plain text so no
 *   stray styles or fonts can sneak in from outside.
 *
 * Apple-style: subtle, soft, clean.
 */

const ALLOWED_TAGS = new Set([
  "P", "BR", "STRONG", "B", "EM", "I", "U", "UL", "OL", "LI", "DIV", "SPAN",
]);

// Strip everything except allowed tags. Keeps inline formatting + lists,
// drops scripts/styles/attributes.
function sanitize(html) {
  if (!html) return "";
  const wrap = document.createElement("div");
  wrap.innerHTML = html;
  const walk = (node) => {
    const children = Array.from(node.childNodes);
    children.forEach((child) => {
      if (child.nodeType === 1) {
        if (!ALLOWED_TAGS.has(child.tagName)) {
          const text = document.createTextNode(child.textContent || "");
          child.replaceWith(text);
        } else {
          [...child.attributes].forEach((attr) => child.removeAttribute(attr.name));
          walk(child);
        }
      } else if (child.nodeType === 8) {
        child.remove();
      }
    });
  };
  walk(wrap);
  return wrap.innerHTML.trim();
}

const RichTextField = ({
  value = "",
  onChange,
  placeholder = "",
  minHeight = 80,
  className = "",
  disabled = false,
}) => {
  const ref = useRef(null);
  const [focused, setFocused] = useState(false);

  // Sync external value into the editor only when it actually differs from
  // the live DOM (prevents caret jumps while typing).
  useEffect(() => {
    if (!ref.current) return;
    const current = ref.current.innerHTML;
    if (value !== current) {
      ref.current.innerHTML = value || "";
    }
  }, [value]);

  const handleInput = () => {
    if (!ref.current || !onChange) return;
    onChange(sanitize(ref.current.innerHTML));
  };

  const handleBlur = () => {
    setFocused(false);
    handleInput();
  };

  const handlePaste = (e) => {
    // Force plain-text paste so external styles never sneak in.
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
  };

  const isEmpty =
    !value ||
    value.replace(/<[^>]+>/g, "").trim() === "" &&
      !/<br\s*\/?>/i.test(value) &&
      !/<li/i.test(value);

  return (
    <div
      className={`rtf rtf--toolbar-hidden ${focused ? "is-focused" : ""} ${
        disabled ? "is-disabled" : ""
      } ${className}`}
    >
      <div className="rtf__editor-wrap">
        <div
          ref={ref}
          className="rtf__editor"
          style={{ minHeight }}
          contentEditable={!disabled}
          suppressContentEditableWarning
          spellCheck
          onInput={handleInput}
          onBlur={handleBlur}
          onFocus={() => setFocused(true)}
          onPaste={handlePaste}
          data-placeholder={placeholder}
        />
        {isEmpty && placeholder && (
          <div className="rtf__placeholder">{placeholder}</div>
        )}
      </div>
    </div>
  );
};

export default RichTextField;
